import { useEffect, useState } from 'react'
import { useHistory, Link } from 'react-router-dom';
import { CourseCreate } from "services/courseService"
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { fetchLicenseClasses } from 'store/licenseSlice'
import CustomDatePicker from 'components/DatePickerVi'

import { toast } from 'react-hot-toast'
import { ArrowLeft, Save } from 'lucide-react';

export default function NewCoursePage() {
    const router = useHistory()
    const dispatch: AppDispatch = useDispatch()

    // Redux state
    const licenseClasses = useSelector((state: RootState) => state.licenseClasses.items)
    const licenseLoading = useSelector((state: RootState) => state.licenseClasses.loading)

    // Form state
    const [formData, setFormData] = useState<any>({
        name: '',
        classCode: '',
        startDate: null as Date | null,
        endDate: null as Date | null,
        openingDate: null as Date | null,
        closingDate: null as Date | null,
        registrationDate: null as Date | null,
        licenseClassId: ''
    })
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    // Fetch license classes
    useEffect(() => {
        if (licenseClasses.length === 0) {
            dispatch(fetchLicenseClasses())
        }
    }, [dispatch, licenseClasses.length])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData((prev: any) => ({
            ...prev,
            [name]: value
        }))
    }
    const handleDateChange = (type: 'start' | 'end' | 'opening' | 'closing' | 'registration') => (date: Date | null) => {
        if (type === 'start') {
            setFormData((prev: any) => ({
                ...prev,
                startDate: date,
                // Reset endDate nếu ngày bắt đầu mới > ngày kết thúc hiện tại
                endDate: (date && prev.endDate && date > prev.endDate) ? null : prev.endDate
            }))
        }
        else if (type === 'opening') {
            setFormData((prev: any) => ({ ...prev, openingDate: date }))
        }
        else if (type === 'closing') {
            setFormData((prev: any) => ({ ...prev, closingDate: date }))
        }
        else if (type === 'registration') {
            setFormData((prev: any) => ({ ...prev, registrationDate: date }))
        }
        else {
            setFormData((prev: any) => ({ ...prev, endDate: date }))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validate required fields
        if (!formData.name || !formData.licenseClassId || !formData.startDate || !formData.endDate
            || !formData.classCode || !formData.openingDate || !formData.registrationDate || !formData.closingDate) {
            setError('Vui lòng điền đầy đủ thông tin bắt buộc')
            return
        }

        // Validate date
        if (new Date(formData.startDate) > new Date(formData.endDate)) {
            setError('Ngày kết thúc phải sau ngày bắt đầu')
            return
        }

        setIsLoading(true)
        setError('')

        try {
            const response = await CourseCreate(formData)
            if (response.result) {
                toast.success('Tạo khóa học thành công')
                router.push('/courses')
            } else {
                throw new Error(response.message || 'Tạo khóa học thất bại')
            }
        } catch (err: any) {
            setError(err.message || 'Đã xảy ra lỗi khi tạo khóa học mới')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <Link
                    to="/courses"
                    className="flex items-center text-blue-600 hover:text-blue-800"
                >
                    <ArrowLeft className="mr-2" />
                    Quay lại
                </Link>
                <h1 className="text-3xl font-bold text-gray-800">Thêm khóa học mới</h1>
                <div></div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl mx-auto">
                {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Tên khóa học *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                required
                                placeholder="Tên khóa học"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="classCode" className="block text-sm font-medium text-gray-700 mb-1">
                                Mã khóa *
                            </label>
                            <input
                                type="text"
                                id="classCode"
                                name="classCode"
                                required
                                value={formData.classCode}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="VD: B1-0725"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="licenseClassId" className="block text-sm font-medium text-gray-700 mb-1">
                                Hạng đào tạo *
                            </label>
                            <select
                                id="licenseClassId"
                                name="licenseClassId"
                                value={formData.licenseClassId}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                required
                                disabled={licenseLoading}
                            >
                                <option value="">Chọn hạng đào tạo</option>
                                {licenseClasses.map((lc) => (
                                    <option key={lc.id} value={lc.id}>
                                        {lc.name}
                                    </option>
                                ))}
                            </select>
                            {licenseLoading && (
                                <p className="text-sm text-gray-500 mt-1">Đang tải danh sách hạng...</p>
                            )}
                        </div>
                        <div className="">
                            <label htmlFor="registrationDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày đăng ký ST *
                            </label>
                            <CustomDatePicker
                                selected={formData.registrationDate}
                                onChange={handleDateChange('registration')}
                                placeholder="Chọn ngày khai giảng"
                                required
                                id="registrationDate"
                                name="registrationDate"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="openingDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày khai giảng *
                            </label>
                            <CustomDatePicker
                                selected={formData.openingDate}
                                onChange={handleDateChange('opening')}
                                placeholder="Chọn ngày khai giảng"
                                required
                                id="openingDate"
                                name="openingDate"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="closingDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày bế giảng *
                            </label>
                            <CustomDatePicker
                                selected={formData.closingDate}
                                onChange={handleDateChange('closing')}
                                placeholder="Chọn ngày bế giảng"
                                required
                                id="closingDate"
                                name="closingDate"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày bắt đầu *
                            </label>
                            <CustomDatePicker
                                selected={formData.startDate}
                                onChange={handleDateChange('start')}
                                placeholder="Chọn ngày bắt đầu"
                                required
                                id="startDate"
                                name="startDate"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày kết thúc *
                            </label>
                            <CustomDatePicker
                                selected={formData.endDate}
                                onChange={handleDateChange('end')}
                                placeholder="Chọn ngày kết thúc"
                                minDate={formData.startDate || new Date()}
                                required
                                id="endDate"
                                name="endDate"
                            />
                        </div>

                    </div>

                    <div className="flex justify-end mt-6">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Đang tạo...
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2" />
                                    Tạo khóa học
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}